/**
 * Currency conversion utilities
 * 
 * Uses Firebase Extension "Currency Exchange" for exchange rates stored in Firestore.
 * Falls back to ExchangeRate-API if extension data is not available.
 * Supports offline currency conversion using cached rates.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type CurrencyCode = string; // ISO 4217 currency code (USD, EUR, etc.)

// Currency symbols - comprehensive list
export const CURRENCY_SYMBOLS: Record<string, string> = {
  // Major currencies
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
  CNY: '¥',
  
  // Americas
  CAD: 'C$',
  MXN: 'MX$',
  BRL: 'R$',
  ARS: '$',
  CLP: '$',
  COP: '$',
  PEN: 'S/',
  UYU: '$U',
  JMD: 'J$',
  TTD: 'TT$',
  
  // Europe
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  ISK: 'kr',
  RUB: '₽',
  UAH: '₴',
  TRY: '₺',
  
  // Asia Pacific
  KRW: '₩',
  INR: '₹',
  SGD: 'S$',
  HKD: 'HK$',
  TWD: 'NT$',
  THB: '฿',
  MYR: 'RM',
  PHP: '₱',
  IDR: 'Rp',
  VND: '₫',
  PKR: '₨',
  BDT: '৳',
  LKR: 'Rs',
  NPR: '₨',
  MMK: 'K',
  KHR: '៛',
  LAK: '₭',
  
  // Oceania
  AUD: 'A$',
  NZD: 'NZ$',
  FJD: 'FJ$',
  
  // Middle East
  AED: 'د.إ',
  SAR: '﷼',
  QAR: '﷼',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: '﷼',
  JOD: 'د.ا',
  ILS: '₪',
  EGP: 'E£',
  LBP: 'ل.ل',
  
  // Africa
  ZAR: 'R',
  MAD: 'د.م.',
  NGN: '₦',
  KES: 'KSh',
  GHS: 'GH₵',
  TZS: 'TSh',
  UGX: 'USh',
  ETB: 'Br',
  MUR: '₨',
  TND: 'د.ت',
};

// AsyncStorage key for persistent cache
const EXCHANGE_RATE_CACHE_KEY = '@tripflow_exchange_rates_cache';

// In-memory cache for quick access
let exchangeRateCache: Map<string, { rate: number; timestamp: number }> = new Map();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds (as per roadmap)

/**
 * Get currency symbol for a currency code
 */
export const getCurrencySymbol = (currencyCode: CurrencyCode): string => {
  return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] || currencyCode.toUpperCase();
};

/**
 * Format amount with currency
 */
export const formatCurrency = (
  amount: number,
  currencyCode: CurrencyCode = 'USD',
  showSymbol: boolean = true
): string => {
  const symbol = showSymbol ? getCurrencySymbol(currencyCode) : '';
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  // Different currencies have different symbol placement
  if (currencyCode === 'USD' || currencyCode === 'CAD' || currencyCode === 'AUD') {
    return `${symbol}${formattedAmount}`;
  } else if (currencyCode === 'EUR' || currencyCode === 'GBP') {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${symbol}`;
  }
};

/**
 * Load exchange rate cache from AsyncStorage
 */
async function loadCacheFromStorage(): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(EXCHANGE_RATE_CACHE_KEY);
    if (stored) {
      const cacheData: Record<string, { rate: number; timestamp: number }> = JSON.parse(stored);
      const now = Date.now();
      
      // Load valid entries into memory cache
      for (const [key, value] of Object.entries(cacheData)) {
        if (now - value.timestamp < CACHE_DURATION) {
          exchangeRateCache.set(key, value);
        }
      }
    }
  } catch (error) {
    console.warn('[Currency] Error loading cache from storage:', error);
  }
}

/**
 * Save exchange rate cache to AsyncStorage
 */
async function saveCacheToStorage(): Promise<void> {
  try {
    const cacheData: Record<string, { rate: number; timestamp: number }> = {};
    exchangeRateCache.forEach((value, key) => {
      cacheData[key] = value;
    });
    await AsyncStorage.setItem(EXCHANGE_RATE_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('[Currency] Error saving cache to storage:', error);
  }
}

/**
 * Get cached rate from memory or persistent storage
 */
async function getCachedRate(cacheKey: string): Promise<number | null> {
  // Check in-memory cache first
  const cached = exchangeRateCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.rate;
  }
  
  // Load from storage if memory cache is empty
  if (exchangeRateCache.size === 0) {
    await loadCacheFromStorage();
    const storedCached = exchangeRateCache.get(cacheKey);
    if (storedCached && Date.now() - storedCached.timestamp < CACHE_DURATION) {
      return storedCached.rate;
    }
  }
  
  return null;
}

/**
 * Get exchange rate from Firebase Extension "Currency Exchange" or fallback to API
 * 
 * The Firebase Extension stores exchange rates in Firestore at:
 * - Collection: `exchangeRates` (or as configured in extension)
 * - Document ID: Base currency code (e.g., "USD")
 * - Field: Target currency code (e.g., "EUR": 0.85)
 * 
 * Falls back to ExchangeRate-API if extension data is not available.
 * Uses cached rates when offline or API fails.
 */
export const getExchangeRate = async (
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): Promise<number> => {
  // Same currency
  if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
    return 1.0;
  }
  
  const cacheKey = `${fromCurrency.toUpperCase()}_${toCurrency.toUpperCase()}`;
  
  // Check cache first (memory or persistent)
  const cachedRate = await getCachedRate(cacheKey);
  if (cachedRate !== null) {
    return cachedRate;
  }
  
  // Try to get rate from Firebase Extension first
  let rate: number | null = null;
  try {
    rate = await fetchExchangeRateFromFirebaseExtension(fromCurrency, toCurrency);
  } catch (error) {
    console.warn('[Currency] Firebase Extension not available, falling back to API:', error);
  }
  
  // Fallback to external API if extension data not available
  if (rate === null) {
    try {
      rate = await fetchExchangeRateFromAPI(fromCurrency, toCurrency);
    } catch (error) {
      console.error('[Currency] API fetch failed, checking for cached rate:', error);
      
      // If API fails, try to use any cached rate (even if expired) as last resort
      const expiredCached = exchangeRateCache.get(cacheKey);
      if (expiredCached) {
        console.warn(`[Currency] Using expired cached rate (${Math.round((Date.now() - expiredCached.timestamp) / (60 * 60 * 1000))}h old) for offline conversion`);
        return expiredCached.rate;
      }
      
      // If no cache available and API fails, throw error instead of returning 1.0
      throw new Error(`Unable to fetch exchange rate for ${fromCurrency} -> ${toCurrency} and no cached rate available`);
    }
  }
  
  // Cache the rate (both in-memory and persistent)
  exchangeRateCache.set(cacheKey, {
    rate,
    timestamp: Date.now(),
  });
  
  // Save to persistent storage (async, don't wait)
  saveCacheToStorage().catch(err => {
    console.warn('[Currency] Failed to save cache to storage:', err);
  });
  
  return rate;
};

/**
 * Fetch exchange rate from Firebase Extension "Currency Exchange"
 * 
 * The extension typically stores rates in Firestore. Common collection names:
 * - `exchangeRates` (default)
 * - `currencyExchangeRates`
 * - `fxRates`
 * 
 * Document structure example:
 * {
 *   "USD": { "EUR": 0.85, "GBP": 0.73, ... },
 *   "EUR": { "USD": 1.18, "GBP": 0.86, ... },
 *   ...
 * }
 * 
 * Or flat structure:
 * {
 *   "EUR": 0.85,
 *   "GBP": 0.73,
 *   ...
 * }
 */
async function fetchExchangeRateFromFirebaseExtension(
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): Promise<number | null> {
  try {
    const baseCurrency = fromCurrency.toUpperCase();
    const targetCurrency = toCurrency.toUpperCase();
    
    // Try common collection names used by the extension
    const possibleCollections = ['exchangeRates', 'currencyExchangeRates', 'fxRates'];
    
    for (const collectionName of possibleCollections) {
      try {
        // Try to get the base currency document
        const rateDocRef = doc(db, collectionName, baseCurrency);
        const rateDoc = await getDoc(rateDocRef);
        
        if (rateDoc.exists()) {
          const data = rateDoc.data();
          
          // Check if rates are nested (e.g., { "EUR": 0.85, "GBP": 0.73 })
          if (data[targetCurrency] && typeof data[targetCurrency] === 'number') {
            return data[targetCurrency];
          }
          
          // Check if rates are in a nested object (e.g., { "rates": { "EUR": 0.85 } })
          if (data.rates && data.rates[targetCurrency] && typeof data.rates[targetCurrency] === 'number') {
            return data.rates[targetCurrency];
          }
          
          // Check if there's a timestamp to verify data freshness
          const timestamp = data.timestamp || data.updatedAt || data.lastUpdated;
          if (timestamp) {
            let timestampMs: number;
            if (timestamp.toMillis) {
              // Firestore Timestamp
              timestampMs = timestamp.toMillis();
            } else if (timestamp.toDate) {
              // Firestore Timestamp (alternative)
              timestampMs = timestamp.toDate().getTime();
            } else if (typeof timestamp === 'number') {
              timestampMs = timestamp;
            } else {
              timestampMs = new Date(timestamp).getTime();
            }
            
            const age = Date.now() - timestampMs;
            // If data is older than 24 hours, consider it stale (but still use it)
            if (age > 24 * 60 * 60 * 1000) {
              console.warn(`[Currency] Exchange rate data is stale (${Math.round(age / 3600000)}h old)`);
            }
          }
        }
      } catch (error) {
        // Collection doesn't exist or permission denied, try next
        continue;
      }
    }
    
    // Extension data not found
    return null;
  } catch (error) {
    console.error('[Currency] Error fetching from Firebase Extension:', error);
    return null;
  }
}

/**
 * Fetch exchange rate from ExchangeRate-API (free tier, no API key required)
 * Throws error if API call fails (caller should handle with cached rates)
 */
async function fetchExchangeRateFromAPI(
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): Promise<number> {
  // ExchangeRate-API free tier: https://www.exchangerate-api.com/
  // No API key required for free tier (1,500 requests/month)
  // Using open.er-api.com which is the free tier endpoint
  const baseCurrency = fromCurrency.toUpperCase();
  const apiUrl = `https://open.er-api.com/v6/latest/${baseCurrency}`;
  
  console.log(`[Currency] Fetching exchange rate: ${fromCurrency} -> ${toCurrency}`);
  
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error(`[Currency] API returned ${response.status}:`, errorText);
    throw new Error(`Exchange rate API returned ${response.status}: ${errorText}`);
  }
  
  const data = await response.json();
  
  if (data.result === 'error') {
    const errorType = data['error-type'] || 'unknown';
    console.error(`[Currency] ExchangeRate-API error: ${errorType}`, data);
    throw new Error(`ExchangeRate-API error: ${errorType}`);
  }
  
  const targetCurrency = toCurrency.toUpperCase();
  const rate = data.rates?.[targetCurrency];
  
  if (!rate || typeof rate !== 'number' || rate <= 0) {
    console.error(`[Currency] Invalid or missing rate for ${targetCurrency}`);
    throw new Error(`Invalid or missing exchange rate for ${targetCurrency}`);
  }
  
  console.log(`[Currency] Successfully fetched rate: 1 ${fromCurrency} = ${rate} ${toCurrency}`);
  return rate;
}

/**
 * Convert amount from one currency to another
 */
export const convertCurrency = async (
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): Promise<number> => {
  if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
    return amount;
  }
  
  const rate = await getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
};

/**
 * Convert multiple amounts to a base currency
 */
export const convertToBaseCurrency = async (
  amounts: Array<{ amount: number; currency: CurrencyCode }>,
  baseCurrency: CurrencyCode = 'USD'
): Promise<number> => {
  if (!amounts || amounts.length === 0) {
    return 0;
  }

  const base = baseCurrency.toUpperCase();

  // Aggregate by currency first to minimize rate lookups and arithmetic
  const totalsByCurrency = new Map<string, number>();
  for (const entry of amounts) {
    const amount = typeof entry?.amount === 'number' ? entry.amount : Number(entry?.amount);
    if (!Number.isFinite(amount) || amount === 0) continue;
    const currency = String(entry?.currency || base).toUpperCase();
    totalsByCurrency.set(currency, (totalsByCurrency.get(currency) ?? 0) + amount);
  }

  if (totalsByCurrency.size === 0) {
    return 0;
  }

  // Fetch rates in parallel (uses cache internally). This avoids sequential awaits that can
  // make Budget/Financial tabs feel slow in multi-currency trips.
  const currencies = Array.from(totalsByCurrency.keys());
  const rates = new Map<string, number>(
    await Promise.all(
      currencies.map(async (cur) => {
        if (cur === base) return [cur, 1.0] as const;
        const rate = await getExchangeRate(cur, base);
        return [cur, rate] as const;
      }),
    ),
  );

  let total = 0;
  for (const [cur, amount] of totalsByCurrency.entries()) {
    total += amount * (rates.get(cur) ?? 1.0);
  }

  return total;
};

/** Minimal shape for summing expenses in a trip base currency without importing `Expense`. */
export type ExpenseLikeForBaseSum = {
  amount: number;
  currency: string;
  conversion?: {
    toCurrency: string;
    convertedAmount: number;
    exchangeRate: number;
  };
};

/**
 * Sum expense amounts in `baseCurrency`.
 * Uses each expense's stored conversion snapshot when it matches `baseCurrency`;
 * otherwise fetches rates via `convertToBaseCurrency` (cached).
 */
export const sumExpensesInTripBaseCurrency = async (
  expenses: ExpenseLikeForBaseSum[],
  baseCurrency: CurrencyCode,
): Promise<number> => {
  const base = baseCurrency.toUpperCase();
  let total = 0;
  const needFetch: Array<{ amount: number; currency: CurrencyCode }> = [];

  for (const e of expenses) {
    const cur = String(e.currency || base).toUpperCase();
    if (cur === base) {
      total += e.amount;
      continue;
    }
    const conv = e.conversion;
    if (
      conv &&
      String(conv.toCurrency).toUpperCase() === base &&
      typeof conv.convertedAmount === 'number' &&
      Number.isFinite(conv.convertedAmount)
    ) {
      total += conv.convertedAmount;
      continue;
    }
    needFetch.push({ amount: e.amount, currency: cur });
  }

  if (needFetch.length === 0) {
    return total;
  }

  const convertedRest = await convertToBaseCurrency(needFetch, base);
  return total + convertedRest;
};

export const expenseListHasForeignCurrency = (
  expenses: ExpenseLikeForBaseSum[],
  baseCurrency: string,
): boolean => {
  const base = baseCurrency.toUpperCase();
  return expenses.some((e) => String(e.currency || base).toUpperCase() !== base);
};

/**
 * Clear exchange rate cache (useful for testing or forced refresh)
 * Clears both in-memory and persistent cache
 */
export const clearExchangeRateCache = async (): Promise<void> => {
  exchangeRateCache.clear();
  try {
    await AsyncStorage.removeItem(EXCHANGE_RATE_CACHE_KEY);
  } catch (error) {
    console.warn('[Currency] Error clearing persistent cache:', error);
  }
};

/**
 * Get information about the exchange rate cache
 */
export const getExchangeRateCacheInfo = (): { 
  lastUpdated: Date | null; 
  count: number; 
  currencies: string[] 
} => {
  let oldestTimestamp: number | null = null;
  const currencies = new Set<string>();
  
  exchangeRateCache.forEach((value, key) => {
    if (!oldestTimestamp || value.timestamp < oldestTimestamp) {
      oldestTimestamp = value.timestamp;
    }
    // Extract currencies from key format "USD_EUR"
    const [from, to] = key.split('_');
    if (from) currencies.add(from);
    if (to) currencies.add(to);
  });
  
  return {
    lastUpdated: oldestTimestamp ? new Date(oldestTimestamp) : null,
    count: exchangeRateCache.size,
    currencies: Array.from(currencies),
  };
};

/**
 * Force refresh exchange rates for specific currencies
 * Clears cache for those currencies and fetches fresh rates
 */
export const refreshExchangeRates = async (
  currencies: CurrencyCode[],
  baseCurrency: CurrencyCode = 'USD'
): Promise<{ 
  success: boolean; 
  updatedAt: Date; 
  rates: Record<string, number> 
}> => {
  const rates: Record<string, number> = {};
  const errors: string[] = [];
  
  // Clear existing cache for these currency pairs
  for (const currency of currencies) {
    const cacheKey1 = `${currency.toUpperCase()}_${baseCurrency.toUpperCase()}`;
    const cacheKey2 = `${baseCurrency.toUpperCase()}_${currency.toUpperCase()}`;
    exchangeRateCache.delete(cacheKey1);
    exchangeRateCache.delete(cacheKey2);
  }
  
  // Fetch fresh rates
  for (const currency of currencies) {
    if (currency.toUpperCase() === baseCurrency.toUpperCase()) {
      rates[currency.toUpperCase()] = 1.0;
      continue;
    }
    
    try {
      const rate = await getExchangeRate(currency, baseCurrency);
      rates[currency.toUpperCase()] = rate;
    } catch (error) {
      console.error(`[Currency] Failed to refresh rate for ${currency}:`, error);
      errors.push(currency);
    }
  }
  
  // Save updated cache
  await saveCacheToStorage();
  
  return {
    success: errors.length === 0,
    updatedAt: new Date(),
    rates,
  };
};

/**
 * Format dual currency display (e.g., "€50 (≈$54.20)")
 */
export const formatDualCurrency = async (
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): Promise<string> => {
  const converted = await convertCurrency(amount, fromCurrency, toCurrency);
  const fromFormatted = formatCurrency(amount, fromCurrency);
  const toFormatted = formatCurrency(converted, toCurrency);
  
  return `${fromFormatted} (≈${toFormatted})`;
};

